/**
 * Exports the correct local storage option for native and web apps.
 * Includes a typed setter/getter to automatically parse storage content.
 * If the app is running in an embedded environment, it uses sessionStorage.
 * Otherwise, it uses AsyncStorage for native apps and localStorage for web apps.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import isDefined from 'src/utils/isDefined';

const isWeb = Platform.OS === 'web';
const isEmbedded = isWeb && window.top !== window.self;
const storage = isWeb && isEmbedded ? window.sessionStorage : AsyncStorage;

const clear = async (): Promise<void> => {
  if (isWeb) {
    return storage.clear();
  } else {
    return await AsyncStorage.clear();
  }
};

const removeItem = async (key: string): Promise<void> => {
  if (isWeb) {
    return storage.removeItem(key);
  } else {
    return await AsyncStorage.removeItem(key);
  }
};

const getItem = async (key: string): Promise<string | null> => {
  if (isWeb) {
    return storage.getItem(key);
  } else {
    return await AsyncStorage.getItem(key);
  }
};

const setItem = async (key: string, value: string): Promise<void> => {
  if (isWeb) {
    return storage.setItem(key, value);
  } else {
    return await AsyncStorage.setItem(key, value);
  }
};

async function getTypedItem<T>(key: string): Promise<T | undefined> {
  const raw = await getItem(key);
  if (isDefined(raw)) {
    return JSON.parse(raw) as T;
  }
  return undefined;
}
async function setTypedItem<T>(key: string, value: T): Promise<void> {
  await setItem(key, JSON.stringify(value));
}

async function getAllKeys(): Promise<readonly string[]> {
  if (isEmbedded) {
    return Object.keys(storage);
  } else {
    return await AsyncStorage.getAllKeys();
  }
}

async function multiRemove(keys: string[]): Promise<void> {
  if (isEmbedded) {
    keys.forEach((key) => storage.removeItem(key));
  } else {
    return await AsyncStorage.multiRemove(keys);
  }
}

export default {
  getAllKeys,
  multiRemove,
  clear,
  removeItem,
  getItem,
  setItem,
  getTypedItem,
  setTypedItem
};
